const DB_NAME = 'device-identity';
const STORE_NAME = 'identity';
const KEY = 'deviceId';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE_NAME);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function getOrCreateDeviceId(): Promise<string> {
  try {
    const db = await openDb();

    const existing = await new Promise<string | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const req = tx.objectStore(STORE_NAME).get(KEY);
      req.onsuccess = () => resolve(req.result as string | undefined);
      req.onerror = () => reject(req.error);
    });

    if (existing) return existing;

    const id = crypto.randomUUID();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const req = tx.objectStore(STORE_NAME).put(id, KEY);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });

    return id;
  } catch {
    // Fallback to localStorage if IndexedDB is unavailable
    const lsKey = 'elka_device_id';
    let id = localStorage.getItem(lsKey);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(lsKey, id);
    }
    return id;
  }
}

/**
 * Returns a human-readable device name from the User-Agent string.
 * Targets iPhones primarily, with fallbacks for other common platforms.
 */
export function getDeviceName(): string {
  const ua = navigator.userAgent;

  // iOS version extraction: "OS 17_4" or "OS 17_4_1"
  const iosMatch = ua.match(/OS (\d+)[_.](\d+)/);
  const iosVersion = iosMatch ? `iOS ${iosMatch[1]}.${iosMatch[2]}` : null;

  if (/iPhone/.test(ua)) {
    return iosVersion ? `iPhone, ${iosVersion}` : 'iPhone';
  }

  if (/iPad/.test(ua)) {
    return iosVersion ? `iPad, ${iosVersion}` : 'iPad';
  }

  if (/Android/.test(ua)) {
    const androidMatch = ua.match(/Android ([\d.]+)/);
    const androidVersion = androidMatch ? `Android ${androidMatch[1]}` : 'Android';
    const deviceMatch = ua.match(/;\s([^;)]+)\sBuild\//);
    const deviceModel = deviceMatch ? deviceMatch[1].trim() : null;
    return deviceModel ? `${deviceModel}, ${androidVersion}` : androidVersion;
  }

  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux';

  return 'Unknown device';
}
